import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import {
  ActivatedRoute,
  Event as NavigationEvent,
  NavigationStart,
  Router,
  RouterModule
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthUserService } from 'src/app/core/services/auth-user.service';
import { SharedModuleService } from 'src/app/core/services/shared-module.service';
import {
  BREAD_CRUMB,
  breadCrumbUrl,
  headerByUrl,
  breadcrumbNavigationMap
} from 'src/app/core/constants/bread-crumb.constants';
import { USERROLES } from 'src/app/core/common-enums/enum';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class BreadcrumbComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  loginFlag: boolean = false;
  setCurrentName: string | undefined;
  breadCrumb: string[] = [];
  role = '';
  mainHeadingValue = BREAD_CRUMB.MAIN_HEADING;

  currentUrl: string = '';
  previousUrl: string | null = null;
  clientNameFromQuery: string = ''; // New: Store client name from query params
  schemeDetailsName: string = ''; // Store scheme name from localStorage
  profileName: string = ''; // Store profile name (broker/RM/advisor) from localStorage
  isCreateUccFromPortfolio: boolean = false; // Track if navigating from portfolio profile to create UCC
  createUccClientName: string = ''; // Store client name for Create UCC breadcrumb

  private _authService = inject(AuthUserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sharedModuleService = inject(SharedModuleService);

  /**
   * ngOnInit is the first life cycle method of Angular
   * @member ngOnInit
   * @memberof BreadcrumbComponent
   */
  ngOnInit(): void {
    this.sharedModuleService.setNavbarHeading(this.mainHeadingValue);
    this._authService
      .getUserDetailBehaviorSubject()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: { newRoleFinal?: { roleDescription?: string } }) => {
        if (data) {
          this.role = data.newRoleFinal?.roleDescription ?? '';
        }
      });
    this.capturePreviousUrl();

    // Extract client name from query params
    this.extractClientNameFromUrl();

    // Set current URL for initial load
    this.currentUrl = this.router.url;

    // Extract profile name from localStorage
    this.profileName = localStorage.getItem('profileName') || '';

    // Set initial breadcrumb and heading for current route
    const initialFullUrl = this.router.url;
    let initialUrlPath = /\/USR_[A-Z0-9]+(\?.*)?$/.test(initialFullUrl)
      ? initialFullUrl.replace(/\/[A-Z0-9_]+(\?.*)?$/, '')
      : initialFullUrl;

    // Strip scheme-details dynamic parameter
    if (initialUrlPath.includes('/scheme-details/')) {
      initialUrlPath = initialUrlPath.replace(
        /\/scheme-details\/[^/?]+/,
        '/scheme-details'
      );
    }
    if (
      initialUrlPath.match(
        /\/(broker|advisor|relationshipManager)\/scheme-details\/[^/?]+/
      )
    ) {
      initialUrlPath = initialUrlPath.replace(
        /\/scheme-details\/[^/?]+/,
        '/scheme-details'
      );
    }

    // Check if we have a breadcrumb for the full URL with query params
    if (breadCrumbUrl[initialUrlPath]) {
      this.setCurrentName = headerByUrl[initialUrlPath];
      this.breadCrumb = this.replaceBreadcrumbWithProfileName(
        breadCrumbUrl[initialUrlPath]
      );
    } else {
      // Fall back to URL without query params
      const initialUrl = this.router.url.split('?')[0];
      initialUrlPath = /\/USR_[A-Z0-9]+$/.test(initialUrl)
        ? initialUrl.replace(/\/[A-Z0-9_]+$/, '')
        : initialUrl;

      // Strip scheme-details dynamic parameter again
      if (initialUrlPath.includes('/scheme-details/')) {
        initialUrlPath = initialUrlPath.replace(
          /\/scheme-details\/[^/?]+/,
          '/scheme-details'
        );
      }
      if (
        initialUrlPath.match(
          /\/(broker|advisor|relationshipManager)\/scheme-details\/[^/?]+/
        )
      ) {
        initialUrlPath = initialUrlPath.replace(
          /\/scheme-details\/[^/?]+/,
          '/scheme-details'
        );
      }

      this.setCurrentName = headerByUrl[initialUrlPath];
      this.breadCrumb = this.replaceBreadcrumbWithProfileName(
        breadCrumbUrl[initialUrlPath] ?? []
      );
    }

    // Add dynamic client name for edit-client-details
    if (
      initialUrlPath === '/client/edit-client-details' &&
      this.clientNameFromQuery
    ) {
      this.breadCrumb = [...this.breadCrumb, this.clientNameFromQuery];
    }

    // Add dynamic client name for client-onboarding in complete mode
    // Only do this if NOT coming from portfolio profile (i.e., direct sidebar navigation)
    // Skip this entirely if we have source=createUccFromPortfolio to avoid adding duplicate client name
    if (
      initialUrlPath === '/client/client-onboarding' &&
      this.isCompleteProfileMode() &&
      this.clientNameFromQuery &&
      !this.router.url.includes('source=createUccFromPortfolio')
    ) {
      this.breadCrumb = this.replaceBreadcrumbWithProfileName(
        breadCrumbUrl['/client/client-onboarding?mode=complete'] ?? []
      );
      this.setCurrentName =
        headerByUrl['/client/client-onboarding?mode=complete'];
      this.breadCrumb = [...this.breadCrumb, this.clientNameFromQuery];
    }

    // Handle Create UCC from portfolio profile flow BEFORE calling routerEvent
    // This ensures it overrides any previous breadcrumb setup
    if (
      initialUrlPath === '/client/client-onboarding' &&
      this.isCompleteProfileMode() &&
      this.router.url.includes('source=createUccFromPortfolio')
    ) {
      this.updateCreateUccBreadcrumb();
    }

    this.sharedModuleService.setNavbarHeading(this.setCurrentName);
    this.routerEvent();
  }

  /**
   * Track current and previous URL (with query params)
   */
  capturePreviousUrl(): void {
    this.router.events
      .pipe(
        filter((event: NavigationEvent) => event instanceof NavigationStart)
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationStart) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        // Extract client name when URL changes
        this.extractClientNameFromUrl(event.url);
      });
  }

  /**
   * Extract client name from URL query parameters
   */
  private extractClientNameFromUrl(url?: string): void {
    const urlToCheck = url || this.router.url;
    const urlParams = new URLSearchParams(urlToCheck.split('?')[1] || '');
    this.clientNameFromQuery = urlParams.get('clientName') || '';
  }

  /**
   * method to get the current url for bread crumb
   * @method routerEvent
   * @memberof BreadcrumbComponent
   */
  routerEvent(): void {
    this.router.events
      .pipe(
        filter((event: NavigationEvent) => event instanceof NavigationStart)
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationStart) => {
        // Extract client name
        this.extractClientNameFromUrl(event.url);

        // Handle URL path
        const fullUrl = event.url;
        let urlPath = /\/USR_[A-Z0-9]+(\?.*)?$/.test(fullUrl)
          ? fullUrl.replace(/\/[A-Z0-9_]+(\?.*)?$/, '')
          : fullUrl;

        // Strip scheme-details dynamic parameter (e.g., /scheme-details/44739 -> /scheme-details)
        if (urlPath.includes('/scheme-details/')) {
          urlPath = urlPath.replace(
            /\/scheme-details\/[^/?]+/,
            '/scheme-details'
          );
        }
        // Also handle role-based scheme-details routes
        if (
          urlPath.match(
            /\/(broker|advisor|relationshipManager)\/scheme-details\/[^/?]+/
          )
        ) {
          urlPath = urlPath.replace(
            /\/scheme-details\/[^/?]+/,
            '/scheme-details'
          );
        }

        // Check if breadcrumb exists
        if (breadCrumbUrl[urlPath]) {
          this.setCurrentName = headerByUrl[urlPath];
          this.breadCrumb = this.replaceBreadcrumbWithProfileName(
            breadCrumbUrl[urlPath]
          );
        } else {
          // Fallback to URL without query params
          const headerUrl = event.url.split('?')[0];
          urlPath = /\/USR_[A-Z0-9]+$/.test(headerUrl)
            ? headerUrl.replace(/\/[A-Z0-9_]+$/, '')
            : headerUrl;

          // Strip scheme-details dynamic parameter again for fallback
          if (urlPath.includes('/scheme-details/')) {
            urlPath = urlPath.replace(
              /\/scheme-details\/[^/?]+/,
              '/scheme-details'
            );
          }
          if (
            urlPath.match(
              /\/(broker|advisor|relationshipManager)\/scheme-details\/[^/?]+/
            )
          ) {
            urlPath = urlPath.replace(
              /\/scheme-details\/[^/?]+/,
              '/scheme-details'
            );
          }

          this.setCurrentName = headerByUrl[urlPath];
          this.breadCrumb = breadCrumbUrl[urlPath] ?? [];
        }

        // Add dynamic client name for edit-client-details
        if (
          urlPath === '/client/edit-client-details' &&
          this.clientNameFromQuery
        ) {
          this.breadCrumb = [...this.breadCrumb, this.clientNameFromQuery];
        }

        // Add dynamic client name for client-onboarding in complete mode
        // Only do this if NOT coming from portfolio profile (i.e., direct sidebar navigation)
        if (
          urlPath === '/client/client-onboarding' &&
          this.isCompleteProfileMode(event.url) &&
          this.clientNameFromQuery &&
          !event.url.includes('source=createUccFromPortfolio')
        ) {
          this.breadCrumb = this.replaceBreadcrumbWithProfileName(
            breadCrumbUrl['/client/client-onboarding?mode=complete'] ?? []
          );
          this.setCurrentName =
            headerByUrl['/client/client-onboarding?mode=complete'];
          this.breadCrumb = [...this.breadCrumb, this.clientNameFromQuery];
        }

        // Handle dynamic client name for client portfolio
        if (urlPath === '/client-portfolio') {
          if (this.isTransactionUrl(this.previousUrl || '')) {
            const transactionClientName = localStorage.getItem(
              'transactionClientName'
            );
            if (transactionClientName) {
              localStorage.setItem('currentClientName', transactionClientName);
            }
          }
          this.updateClientNameInBreadcrumb();
        }

        // Update profile name from localStorage
        this.profileName = localStorage.getItem('profileName') || '';

        // Handle dynamic scheme name for scheme-details page
        if (this.isSchemeDetailsPage(urlPath)) {
          this.updateSchemeNameInBreadcrumb();
        }

        // Add client name to transaction pages when navigating from holdings
        const decodedUrlPath = decodeURIComponent(urlPath);
        if (
          decodedUrlPath.includes('/transaction/') ||
          decodedUrlPath.includes('/fresh/transaction/')
        ) {
          const transactionClientName = localStorage.getItem(
            'transactionClientName'
          );
          if (transactionClientName) {
            this.breadCrumb = [...this.breadCrumb, transactionClientName];
          }
        }

        // Handle Create UCC from portfolio profile flow (only override if coming from portfolio)
        if (
          urlPath === '/client/client-onboarding' &&
          this.isCompleteProfileMode(event.url) &&
          event.url.includes('source=createUccFromPortfolio')
        ) {
          this.updateCreateUccBreadcrumb();
        }

        this.sharedModuleService.setNavbarHeading(this.setCurrentName);
      });
  }

  /**
   * Update breadcrumb with client name from localStorage
   */
  private updateClientNameInBreadcrumb(): void {
    try {
      // First check if we have a current client name set (e.g., from transaction context)
      const currentClientName = localStorage.getItem('currentClientName');
      if (currentClientName) {
        this.breadCrumb = this.breadCrumb.map((crumb) =>
          crumb === 'Client Portfolio' ? currentClientName : crumb
        );
        // Clear the temporary currentClientName after using it
        // This ensures it doesn't affect subsequent breadcrumb updates
        localStorage.removeItem('currentClientName');
        return;
      }

      const clientDetailsStr = localStorage.getItem('clientDetails');
      if (clientDetailsStr) {
        const clientDetails = JSON.parse(clientDetailsStr);

        // Try new structure first: direct clientName field
        let fullName = clientDetails.clientName || '';

        // If not found, fall back to old structure: separate firstName, middleName, lastName
        if (!fullName) {
          const firstName = clientDetails.clientFirstName || '';
          const middleName = clientDetails.clientMiddleName || '';
          const lastName = clientDetails.clientLastName || '';

          fullName = [firstName, middleName, lastName]
            .filter((n) => n && n.trim())
            .join(' ')
            .trim();
        }

        if (fullName) {
          this.breadCrumb = this.breadCrumb.map((crumb) =>
            crumb === 'Client Portfolio' ? fullName : crumb
          );
        }
      }
    } catch {
      // fallback silently
    }
  }

  /**
   * Check if the current URL is a scheme-details page
   */
  private isSchemeDetailsPage(urlPath: string): boolean {
    return (
      urlPath.includes('/scheme-details') ||
      urlPath === '/broker/scheme-details' ||
      urlPath === '/advisor/scheme-details' ||
      urlPath === '/relationshipManager/scheme-details'
    );
  }

  /**
   * Update breadcrumb with scheme name from localStorage for scheme-details page
   */
  private updateSchemeNameInBreadcrumb(): void {
    try {
      const schemeName = localStorage.getItem('schemeDetailsName');
      if (schemeName) {
        this.schemeDetailsName = schemeName;
        // Add scheme name to breadcrumb if not already present
        if (!this.breadCrumb.includes(schemeName)) {
          this.breadCrumb = [...this.breadCrumb, schemeName];
        }
      }
    } catch {
      // fallback silently
    }
  }

  goBackFromDetails(): void {
    // If on complete profile page, navigate to client portfolio instead of using previousUrl
    if (
      this.currentUrl.includes('/client/client-onboarding') &&
      this.currentUrl.includes('mode=complete')
    ) {
      this.navigateToClientPortfolio();
      return;
    }

    if (this.previousUrl) {
      this.router.navigateByUrl(this.previousUrl);
    } else {
      this.backRoute();
    }
  }

  /**
   * Get navigation URL for a breadcrumb item
   * @param crumb - The breadcrumb text
   * @returns The navigation URL or null if should use default relative navigation
   */
  getBreadcrumbUrl(crumb: string): string | null {
    // Role-aware navigation for Relationship Managers breadcrumb
    if (crumb === 'Relationship Managers') {
      if (this.role === USERROLES.ADVISOR) {
        return '/advisor/advisors-list?tab=rm';
      }
      return '/broker/brokers-list?tab=rm';
    }

    // Role-aware navigation for profile breadcrumbs
    if (crumb === 'Broker Profile' || crumb === 'Brokers') {
      return '/broker/brokers-list?tab=brokers';
    }

    if (crumb === 'Advisor Profile' || crumb === 'Advisors') {
      return '/advisor/advisors-list?tab=advisors';
    }

    if (crumb === 'RM Profile') {
      if (this.role === USERROLES.ADVISOR) {
        return '/advisor/advisors-list?tab=rm';
      }
      return '/broker/brokers-list?tab=rm';
    }

    // Role-aware navigation for Create UCC breadcrumb
    if (crumb === 'Create UCC') {
      if (this.role === USERROLES.ADVISOR) {
        return '/advisor/create-ucc';
      } else if (this.role === USERROLES.RELATIONSHIP_MANAGER) {
        return '/relationshipManager/create-ucc';
      }
      return '/broker/create-ucc';
    }

    return breadcrumbNavigationMap[crumb] || null;
  }

  /**
   * Navigate to a specific breadcrumb
   * @param crumb - The breadcrumb text
   */
  navigateToBreadcrumb(crumb: string): void {
    const url = this.getBreadcrumbUrl(crumb);
    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  /**
   * Replace profile type labels with actual profile names from localStorage
   */
  private replaceBreadcrumbWithProfileName(breadcrumbs: string[]): string[] {
    const profileName = localStorage.getItem('profileName');
    if (!profileName) {
      return breadcrumbs;
    }

    return breadcrumbs.map((crumb) => {
      if (
        crumb === 'Broker Profile' ||
        crumb === 'RM Profile' ||
        crumb === 'Advisor Profile'
      ) {
        return profileName;
      }
      return crumb;
    });
  }

  /**
   * Navigate directly to clients list based on role
   */
  navigateToClients(): void {
    if (this.role === USERROLES.BROKER_ADMIN) {
      this.router.navigate(['broker/clients']);
    } else if (this.role === USERROLES.ADVISOR) {
      this.router.navigate(['advisor/clients']);
    } else if (this.role === USERROLES.RELATIONSHIP_MANAGER) {
      this.router.navigate(['relationshipManager/clients']);
    } else if (this.role === USERROLES.CLIENT) {
      this.router.navigate(['/client/profile']);
    } else {
      this.router.navigate(['/product-admin/dashboard']);
    }
  }

  /**
   * Check if current URL is in complete profile mode
   */
  private isCompleteProfileMode(url?: string): boolean {
    const urlToCheck = url || this.router.url;
    return urlToCheck.includes('mode=complete');
  }

  /**
   * Update breadcrumb for Create UCC from portfolio profile flow
   * Shows: Home / Clients / [ClientName] / Create UCC
   */
  private updateCreateUccBreadcrumb(): void {
    try {
      const clientName =
        this.clientNameFromQuery ||
        localStorage.getItem('createUccClientName') ||
        '';

      if (clientName) {
        this.setCurrentName = 'Create UCC';
        this.breadCrumb = ['Home', 'Clients', clientName, 'Create UCC'];
        this.isCreateUccFromPortfolio = true;
        this.createUccClientName = clientName;
      }
    } catch {
      // fallback silently
    }
  }

  /**
   * Check if a crumb is the client name in Create UCC flow
   */
  isCreateUccClientName(crumb: string): boolean {
    return (
      this.isCreateUccFromPortfolio &&
      !!this.createUccClientName &&
      crumb === this.createUccClientName
    );
  }

  /**
   * Default back route based on user role
   */
  backRoute(): void {
    if (this.role === USERROLES.BROKER_ADMIN) {
      this.router.navigate(['broker/clients']);
    } else if (this.role === USERROLES.ADVISOR) {
      this.router.navigate(['advisor/clients']);
    } else if (this.role === USERROLES.RELATIONSHIP_MANAGER) {
      this.router.navigate(['relationshipManager/clients']);
    } else if (this.role === USERROLES.CLIENT) {
      this.router.navigate(['/client/profile']);
    } else {
      this.router.navigate(['/product-admin/dashboard']);
    }
  }

  /**
   * Check if current page is a transaction page
   */
  isOnTransactionPage(): boolean {
    const decodedUrl = decodeURIComponent(this.currentUrl || this.router.url);
    return (
      decodedUrl.includes('/transaction/') ||
      decodedUrl.includes('/fresh/transaction/')
    );
  }

  private isTransactionUrl(url: string): boolean {
    const decodedUrl = decodeURIComponent(url || '');
    return (
      decodedUrl.includes('/transaction/') ||
      decodedUrl.includes('/fresh/transaction/')
    );
  }

  /**
   * Check if a crumb is the transaction client name (from localStorage)
   */
  isTransactionClientName(crumb: string): boolean {
    const transactionClientName = localStorage.getItem('transactionClientName');
    return !!transactionClientName && crumb === transactionClientName;
  }

  /**
   * Navigate to client holdings page (similar to list-of-clients clientProfile method)
   */
  navigateToHoldings(): void {
    // Get client user ID from localStorage
    const clientUserId = localStorage.getItem('clientUserId');

    if (!clientUserId) {
      console.error('No clientUserId found in localStorage');
      // Fall back to goBackFromDetails if no clientUserId
      this.goBackFromDetails();
      return;
    }

    // Get the transaction client name from localStorage to update breadcrumb context
    const transactionClientName = localStorage.getItem('transactionClientName');

    // Get client details from localStorage
    const clientDetailsStr = localStorage.getItem('clientDetails');
    const clientCodeBse = localStorage.getItem('clientCodeBse');

    // If we have transaction client name, update the breadcrumb context
    // This ensures that when returning to holdings, the breadcrumb shows the correct client name
    if (transactionClientName) {
      // Parse existing client details if available
      let clientDetails: Record<string, unknown> = {};
      if (clientDetailsStr) {
        try {
          clientDetails = JSON.parse(clientDetailsStr);
        } catch {
          // If parsing fails, use empty object
        }
      }

      // Update client details with transaction client name
      clientDetails.clientName = transactionClientName;

      // Store the updated client details back to localStorage
      localStorage.setItem('clientDetails', JSON.stringify(clientDetails));

      // Also store transactionClientName as a breadcrumb context
      localStorage.setItem('currentClientName', transactionClientName);
    }

    // Navigate to client portfolio with proper query params
    this.router.navigate(['client-portfolio', clientUserId], {
      queryParams: { clientProfileType: 'AllclientsProfile' }
    });

    // Ensure localStorage has the necessary data (in case it was cleared)
    if (clientCodeBse) {
      localStorage.setItem('clientCodeBse', clientCodeBse);
    }
    localStorage.setItem('clientUserId', clientUserId);
  }

  /**
   * Navigate from Create UCC breadcrumb click - handles both portfolio and sidebar flows
   */
  navigateFromCreateUcc(): void {
    // If coming from portfolio profile (isCreateUccFromPortfolio is true)
    if (this.isCreateUccFromPortfolio && this.previousUrl) {
      this.router.navigateByUrl(this.previousUrl);
      return;
    }

    // If coming from sidebar (direct Create UCC navigation)
    // Navigate to client profile using clientUserId
    const clientUserId = localStorage.getItem('clientUserId');
    if (clientUserId) {
      this.router.navigate(['client', clientUserId, 'profile']);
      return;
    }

    // Fallback to clients list
    this.navigateToClients();
  }

  /**
   * Navigate client name from Create UCC breadcrumb - special handling for complete profile page
   * When on complete profile page, navigates to client portfolio instead of using Create UCC flow
   */
  navigateClientNameFromCreateUcc(): void {
    // If on complete profile page, navigate to client portfolio
    if (
      this.currentUrl.includes('/client/client-onboarding') &&
      this.currentUrl.includes('mode=complete')
    ) {
      this.navigateToClientPortfolio();
    } else {
      this.navigateFromCreateUcc();
    }
  }

  /**
   * Navigate to client portfolio/profile page when clicking client name in Create UCC breadcrumb
   */
  navigateToClientPortfolio(): void {
    // If on complete profile page, don't use previousUrl (it's the Create UCC page)
    // Instead navigate directly to client portfolio using clientUserId
    if (
      this.currentUrl.includes('/client/client-onboarding') &&
      this.currentUrl.includes('mode=complete')
    ) {
      const clientUserId = localStorage.getItem('clientUserId');
      if (clientUserId) {
        this.router.navigate(['client-portfolio', clientUserId], {
          queryParams: { clientProfileType: 'AllclientsProfile' }
        });
      } else {
        this.navigateToClients();
      }
      return;
    }

    // For other pages, try to use previousUrl if available
    if (this.previousUrl) {
      this.router.navigateByUrl(this.previousUrl);
      return;
    }

    // Otherwise navigate to client portfolio using clientUserId
    const clientUserId = localStorage.getItem('clientUserId');
    if (clientUserId) {
      this.router.navigate(['client-portfolio', clientUserId], {
        queryParams: { clientProfileType: 'AllclientsProfile' }
      });
    } else {
      // Fallback to clients list
      this.navigateToClients();
    }
  }
}
